/**
 * useTracking — UX Telemetry Hook
 *
 * Lightweight event tracking for planning module UX metrics.
 * Dispatches to backend POST /api/analytics/track (or console in dev).
 *
 * Events tracked:
 * - plan_wizard_start / plan_wizard_step / plan_wizard_complete / plan_wizard_abandon
 * - plan_ai_suggestion_requested / plan_ai_suggestion_accepted / plan_ai_suggestion_dismissed
 * - plan_published / plan_viewed_by_staff / plan_viewed_by_athlete
 * - button_click / page_view / error_encountered
 */
import { useCallback, useRef, useEffect } from 'react';
import { AZURE } from '../services/api';

const IS_DEV = process.env.NODE_ENV === 'development';
const TRACKING_ENDPOINT = '/analytics/track';

// Batching: collect events and flush every 5s or on page unload
let eventBuffer = [];
let flushTimer = null;

async function flushEvents() {
  if (eventBuffer.length === 0) return;
  const batch = [...eventBuffer];
  eventBuffer = [];

  if (IS_DEV) {
    console.groupCollapsed(`[Tracking] Flushing ${batch.length} events`);
    batch.forEach((e) => console.log(e.event, e.properties));
    console.groupEnd();
    return;
  }

  try {
    await AZURE.post(TRACKING_ENDPOINT, { events: batch });
  } catch (err) {
    // Silent fail — never block UX for tracking
    console.warn('[Tracking] Failed to send events:', err.message);
    // Re-queue failed events (max 50 to prevent memory leak)
    eventBuffer = [...batch.slice(-20), ...eventBuffer].slice(0, 50);
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushEvents();
  }, 5000);
}

// Flush on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushEvents();
  });
  window.addEventListener('beforeunload', flushEvents);
}

/**
 * Core track function (non-hook, can be used anywhere)
 */
export function trackEvent(eventName, properties = {}) {
  const event = {
    event: eventName,
    properties: {
      ...properties,
      timestamp: new Date().toISOString(),
      url: window.location.pathname,
      userAgent: navigator.userAgent.substring(0, 100),
    },
  };
  eventBuffer.push(event);
  scheduleFlush();
}

/**
 * Hook: useTracking
 * Provides page-scoped tracking with automatic page_view on mount
 * and time_on_page on unmount.
 */
export function useTracking(pageName, extraProps = {}) {
  const mountTime = useRef(Date.now());

  useEffect(() => {
    trackEvent('page_view', { page: pageName, ...extraProps });

    return () => {
      const duration = Math.round((Date.now() - mountTime.current) / 1000);
      trackEvent('page_leave', { page: pageName, duration_seconds: duration, ...extraProps });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageName]);

  const track = useCallback(
    (eventName, props = {}) => {
      trackEvent(eventName, { page: pageName, ...extraProps, ...props });
    },
    [pageName, extraProps]
  );

  return { track, trackEvent };
}

/**
 * Hook: useWizardTracking
 * Specialized for multi-step wizards — tracks step progression,
 * abandonment, and completion.
 */
export function useWizardTracking(wizardName, totalSteps) {
  const startTime = useRef(Date.now());
  const stepTimes = useRef({});
  const highestStep = useRef(0);

  useEffect(() => {
    trackEvent('wizard_start', { wizard: wizardName, total_steps: totalSteps });

    return () => {
      const duration = Math.round((Date.now() - startTime.current) / 1000);
      if (highestStep.current < totalSteps - 1) {
        trackEvent('wizard_abandon', {
          wizard: wizardName,
          abandoned_at_step: highestStep.current,
          total_steps: totalSteps,
          duration_seconds: duration,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardName]);

  const trackStep = useCallback(
    (stepIndex, stepName) => {
      const now = Date.now();
      const prevStepDuration = stepTimes.current[stepIndex - 1]
        ? Math.round((now - stepTimes.current[stepIndex - 1]) / 1000)
        : 0;

      stepTimes.current[stepIndex] = now;
      highestStep.current = Math.max(highestStep.current, stepIndex);

      trackEvent('wizard_step', {
        wizard: wizardName,
        step: stepIndex,
        step_name: stepName,
        prev_step_duration_seconds: prevStepDuration,
      });
    },
    [wizardName]
  );

  const trackComplete = useCallback(
    (metadata = {}) => {
      const duration = Math.round((Date.now() - startTime.current) / 1000);
      highestStep.current = totalSteps; // Prevent abandon event
      trackEvent('wizard_complete', {
        wizard: wizardName,
        total_steps: totalSteps,
        duration_seconds: duration,
        ...metadata,
      });
    },
    [wizardName, totalSteps]
  );

  return { trackStep, trackComplete };
}

export default useTracking;
